import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import sandeepImage from "../assests/sandeep.webp";
import {
  ArrowRight,
  ArrowUpRight,
  CheckCircle2,
  Target,
  Eye,
  HeartHandshake,
  Trophy,
  Phone,
  Mail,
  MapPin,
  Clock,
} from "lucide-react";
import { ADVISOR } from "../data/mock";
import { useRevealGroup, useCounter } from "../hooks/useReveal";
import {
  PageHero,
  SectionHeading,
  PillBadge,
  CTASection,
} from "../components/Shared";

const ABOUT_PHONE = "(604) 996 6862";
const ABOUT_PHONE_HREF = "tel:+16049966862";
const ABOUT_EMAIL = "info@abhifinancial.com";

const ABOUT_MAP_URL =
  "https://www.google.com/maps/dir/?api=1&destination=8128%20128%20St%20%23302%2C%20Surrey%2C%20BC%20V3W%201R1%2C%20Canada";

const ABOUT_TITLE =
  "About Abhi Insurance Agency | Insurance Advisor Surrey BC | (604) 996 6862";

const ABOUT_META_DESCRIPTION =
  "Founded by Sandeep Ahuja, licensed insurance advisor in Surrey BC since 2000. MDRT member, 8 time Top of the Table. Free consultations 7 days a week.";

const ABOUT_DISCLAIMER =
  "Abhi Insurance Agency Inc. is a licensed insurance advisory based in Surrey, British Columbia. The information on this page is general in nature and is not financial, legal or insurance advice. Coverage terms, eligibility, premiums and program requirements vary by insurer and by individual circumstances, and change over time. Nothing here guarantees eligibility, approval or a particular premium. Speak with a licensed advisor before making any insurance decision, and refer to your policy documents for the terms that actually apply to you.";

  const MOBILE_PAGE_DISCLAIMER =
  "This information is general only and is not financial, legal or insurance advice. Coverage, eligibility and premiums vary. Speak with a licensed advisor before making an insurance decision.";

const ABOUT_SPACING_CSS = `
  #abhi-about-page {
    --about-section-space: 40px;
    --about-card-gap: 24px;
    --about-location-top: 48px;
    --about-location-bottom: 24px;
    --about-cta-top: 24px;
  }

  #abhi-about-page > .about-story-section {
    padding-bottom: var(--about-section-space) !important;
  }

  #abhi-about-page > .about-values-section,
  #abhi-about-page > .about-leadership-section,
  #abhi-about-page > .about-promise-section {
    margin-top: 0 !important;
    margin-bottom: 0 !important;
    padding-top: var(--about-section-space) !important;
    padding-bottom: var(--about-section-space) !important;
  }

  #abhi-about-page > .about-values-section,
  #abhi-about-page > .about-promise-section {
    background: #ffffff !important;
    background-image: none !important;
    box-shadow: none !important;
    isolation: isolate;
  }

  #abhi-about-page > .about-values-section::before,
  #abhi-about-page > .about-values-section::after,
  #abhi-about-page > .about-promise-section::before,
  #abhi-about-page > .about-promise-section::after {
    content: none !important;
    display: none !important;
  }

  #abhi-about-page > .about-testimonials-section {
    margin-top: var(--about-card-gap) !important;
    margin-bottom: 0 !important;
    padding-top: var(--about-section-space) !important;
    padding-bottom: var(--about-section-space) !important;
  }

  #abhi-about-page > .about-location-section {
    margin-top: 0 !important;
    margin-bottom: 0 !important;
    padding-top: var(--about-location-top) !important;
    padding-bottom: var(--about-location-bottom) !important;
  }

  #abhi-about-page > .desktop-cta-section,
  #abhi-about-page > .mobile-cta-section {
    margin-top: 0 !important;
    padding-top: var(--about-cta-top) !important;
  }

  @media (max-width: 1023px) {
    /*
     * Hide Our Promise circular check icons on mobile only.
     */
    #abhi-about-page .about-promise-check-icon {
      display: none !important;
    }

    /*
     * Testimonials mobile alignment.
     */
    #abhi-about-page
      .about-testimonials-section
      .testimonials-navigation {
      justify-content: center !important;
      width: 100%;
    }

    #abhi-about-page
      .about-testimonials-section
      .testimonials-controls {
      justify-content: center !important;
      margin-inline: auto !important;
    }

    #abhi-about-page
      .about-testimonials-section
      .testimonials-quote-mark {
      display: block !important;
      margin-left: auto !important;
      margin-right: auto !important;
    }

    #abhi-about-page
      .about-testimonials-section
      .testimonials-author {
      justify-content: center !important;
      width: 100%;
    }

    #abhi-about-page
      .about-testimonials-section
      .testimonials-author > div {
      text-align: left !important;
    }

    #abhi-about-page
      .about-testimonials-section
      .testimonials-pagination {
      justify-content: center !important;
      width: 100%;
    }

    /*
     * Our Location mobile header.
     */
    #abhi-about-page .about-location-header {
      align-items: center !important;
      text-align: center !important;
    }

    #abhi-about-page .about-location-title {
      flex-direction: column !important;
      align-items: center !important;
      justify-content: center !important;
      text-align: center !important;
    }

    #abhi-about-page .about-location-agency-name {
      width: 100%;
      text-align: center !important;
    }

    /*
     * Address, phone and email use identical columns.
     */
    #abhi-about-page .about-location-detail-row {
      display: grid !important;
      grid-template-columns: 40px minmax(0, 1fr) 16px !important;
      align-items: center !important;
      column-gap: 14px !important;
      text-align: left !important;
    }

    #abhi-about-page .about-location-address-copy {
      grid-column: 2 / 4 !important;
      text-align: left !important;
    }

    #abhi-about-page
      .about-location-detail-row
      > span:nth-child(2) {
      text-align: left !important;
    }

    #abhi-about-page .about-location-detail-row > svg {
      grid-column: 3 !important;
      margin-left: auto !important;
    }

    #abhi-about-page .about-location-hours {
      justify-content: flex-start !important;
      text-align: left !important;
    }
  }

  @media (min-width: 1024px) {
    #abhi-about-page {
      --about-section-space: 48px;
      --about-card-gap: 32px;
      --about-location-top: 64px;
      --about-location-bottom: 32px;
      --about-cta-top: 32px;
    }
  }
`;

const VALUES = [
  {
    icon: Target,
    title: "Our Mission",
    text: "To take the worry and guesswork out of financial protection by comparing plans across companies, so every client is confident they hold the best coverage for their needs and their budget.",
  },
  {
    icon: Eye,
    title: "Our Vision",
    text: "To be the most trusted personal insurance advisory in British Columbia for Canadians, newcomers and visitors alike, known for honesty, patience and results.",
  },
  {
    icon: HeartHandshake,
    title: "Our Values",
    text: "Transparency with no hidden fees. Tailored advice instead of one size fits all sales. Availability 7 days a week, because emergencies do not keep business hours.",
  },
];

const ABOUT_CREDENTIALS = [
  "MDRT Member since 2010",
  "Advocis Member",
  "CHS Designation",
  "8 Times Top of the Table",
  "Fully Licensed in British Columbia",
  "5 Star Client Reviews",
];

const ABOUT_GUARANTEES = [
  {
    id: "consultations",
    title: "Free Advice Consultations",
    text: "Speak with a licensed advisor by phone or in person, completely free, with no obligation.",
  },
  {
    id: "fees",
    title: "No Hidden Fees",
    text: "Total transparency from your first consultation to your final policy. What we quote is what you pay.",
  },
  {
    id: "claims",
    title: "In House Claim Assistance",
    text: "A dedicated claim assistance manager works directly with clients when you need support most.",
  },
  {
    id: "refunds",
    title: "Same Day Partial Refunds",
    text: "Partial refunds are processed the very same day.",
  },
  {
    id: "reviews",
    title: "5 Star Rated Service",
    text: "Consistently rated 5 stars by clients across Surrey and the Lower Mainland.",
  },
  {
    id: "recognition",
    title: "Industry Recognition",
    text: "Top of the Table 8 times, MDRT member since 2010, Advocis member with CHS designation.",
  },
];

const GUARANTEE_DESKTOP_ORDER = {
  consultations: "lg:order-1",
  fees: "lg:order-2",
  claims: "lg:order-3",
  reviews: "lg:order-4",
  recognition: "lg:order-5",
  refunds: "lg:order-6",
};

function useAboutMetadata() {
  useEffect(() => {
    const previousTitle = document.title;
    const existingDescription = document.head.querySelector(
      'meta[name="description"]'
    );

    const previousDescription = existingDescription
      ? existingDescription.getAttribute("content")
      : null;

    const description =
      existingDescription || document.createElement("meta");

    if (!existingDescription) {
      description.setAttribute("name", "description");
      document.head.appendChild(description);
    }

    document.title = ABOUT_TITLE;
    description.setAttribute("content", ABOUT_META_DESCRIPTION);

    return () => {
      document.title = previousTitle;

      if (!existingDescription) {
        description.remove();
      } else if (previousDescription === null) {
        description.removeAttribute("content");
      } else {
        description.setAttribute("content", previousDescription);
      }
    };
  }, []);
}

function Milestone({ value, suffix, label, index }) {
  const [ref, currentValue] = useCounter(value);

  return (
    <div
      ref={ref}
      data-reveal
      style={{ transitionDelay: `${index * 90}ms` }}
      className={[
        "group rounded-2xl bg-white p-8 text-center",
        "shadow-[0_2px_16px_rgba(0,111,185,0.07)]",
        "transition-all duration-500",
        "hover:-translate-y-1",
        "hover:shadow-[0_18px_38px_rgba(0,111,185,0.12)]",
        "lg:rounded-none lg:bg-transparent lg:px-6 lg:py-10",
        "lg:shadow-none",
        "lg:hover:-translate-y-0 lg:hover:shadow-none",
        index > 0 ? "lg:border-l lg:border-[#CFE3EF]" : "",
      ].join(" ")}
    >
      <p className="font-heading text-4xl font-extrabold text-[#006FB9]">
        {currentValue}
        <span className="text-[#54AEDB]">{suffix}</span>
      </p>

      <p className="mt-2 text-sm font-medium leading-relaxed text-[#526A7D]">
        {label}
      </p>

      <span className="mx-auto mt-4 hidden h-[3px] w-0 rounded-full bg-[#0785C7] transition-all duration-500 group-hover:w-12 lg:block" />
    </div>
  );
}

function AboutLocationSection() {
  return (
    <section
      className="about-location-section bg-white"
      aria-labelledby="about-location-heading"
    >
      <div className="mx-auto max-w-7xl px-6">
        <div
          data-reveal
          className="overflow-hidden rounded-3xl border border-[#E1ECF3] bg-white shadow-[0_12px_36px_rgba(7,59,103,0.08)]"
        >
          <div className="about-location-header flex flex-col gap-5 border-b border-[#E1ECF3] bg-[#F4F7FB] px-6 py-6 sm:px-8 lg:flex-row lg:items-center lg:justify-between">
            <div className="about-location-title flex items-center gap-4">
              {/* <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-[#006FB9]">
                <MapPin
                  aria-hidden="true"
                  className="h-6 w-6 text-white"
                />
              </span> */}

              <h2
                id="about-location-heading"
                className="font-heading text-3xl font-extrabold leading-[1.15] tracking-tight text-[#0B2748] sm:text-4xl"
              >
                Our <span className="text-[#0785C7]">Location</span>
              </h2>
            </div>

            <h3 className="about-location-agency-name font-heading text-lg font-bold text-[#0B2748] lg:text-right">
              Abhi Insurance Agency Inc.
            </h3>
          </div>

          <address className="grid grid-cols-1 not-italic md:grid-cols-3">
            <a
  href={ABOUT_MAP_URL}
  target="_blank"
  rel="noopener noreferrer"
  aria-label="Open Abhi Insurance Agency location in Google Maps"
  className="about-location-detail-row group flex min-w-0 items-center gap-3 px-6 py-6 transition-colors hover:bg-[#F4F7FB] sm:px-8"
>
  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-[#E2F2FA] transition-colors group-hover:bg-[#006FB9]">
    <MapPin
      aria-hidden="true"
      className="h-5 w-5 text-[#006FB9] transition-colors group-hover:text-white"
    />
  </span>

  <span className="about-location-address-copy min-w-0 text-[15px] font-medium leading-relaxed text-[#193B58]">
    8128 128 St #302

    <span className="block font-normal text-[#526A7D]">
      Surrey, BC V3W 1R1
    </span>
  </span>

  <ArrowUpRight
    aria-hidden="true"
    className="ml-auto h-4 w-4 shrink-0 text-[#006FB9]"
  />
</a>

            <a
              href={ABOUT_PHONE_HREF}
              className="about-location-detail-row group flex min-w-0 items-center gap-3 border-t border-[#E1ECF3] px-6 py-6 transition-colors hover:bg-[#F4F7FB] sm:px-8 md:border-l md:border-t-0"
            >
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-[#E2F2FA] transition-colors group-hover:bg-[#006FB9]">
                <Phone
                  aria-hidden="true"
                  className="h-5 w-5 text-[#006FB9] transition-colors group-hover:text-white"
                />
              </span>

              <span className="min-w-0">
                <span className="block text-xs font-medium text-[#526A7D]">
                  Phone:
                </span>

                <span className="mt-1 block text-[15px] font-semibold text-[#006FB9] group-hover:underline">
                  {ABOUT_PHONE}
                </span>
              </span>

              <ArrowUpRight
                aria-hidden="true"
                className="ml-auto h-4 w-4 shrink-0 text-[#006FB9]"
              />
            </a>

            <a
              href={`mailto:${ABOUT_EMAIL}`}
              className="about-location-detail-row group flex min-w-0 items-center gap-3 border-t border-[#E1ECF3] px-6 py-6 transition-colors hover:bg-[#F4F7FB] sm:px-8 md:border-l md:border-t-0"
            >
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-[#E2F2FA] transition-colors group-hover:bg-[#006FB9]">
                <Mail
                  aria-hidden="true"
                  className="h-5 w-5 text-[#006FB9] transition-colors group-hover:text-white"
                />
              </span>

              <span className="min-w-0">
                <span className="block text-xs font-medium text-[#526A7D]">
                  Email:
                </span>

                <span className="mt-1 block break-words text-[15px] font-semibold text-[#006FB9] group-hover:underline">
                  {ABOUT_EMAIL}
                </span>
              </span>

              <ArrowUpRight
                aria-hidden="true"
                className="ml-auto h-4 w-4 shrink-0 text-[#006FB9]"
              />
            </a>
          </address>

          <div className="about-location-hours flex items-center gap-3 bg-[#073B67] px-6 py-4 text-[#D4E7F1] sm:px-8">
            <Clock
              aria-hidden="true"
              className="h-5 w-5 shrink-0 text-[#B9DFF3]"
            />

            <p className="text-sm font-medium leading-relaxed">
              Open 7 days a week, 9:00 to 17:00
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function About() {
  const ref = useRevealGroup();

  useAboutMetadata();

  return (
    <div ref={ref} id="abhi-about-page">
      <style>{ABOUT_SPACING_CSS}</style>

      <PageHero
        badge="About Us"
        title="Because Your Peace Matters"
        subtitle="Advising Canadians, newcomers and visitors on their insurance since 2000."
        image="https://images.pexels.com/photos/4260643/pexels-photo-4260643.jpeg?auto=compress&cs=tinysrgb&w=2000"
        imageAlt="A happy family spending quality time together at home"
      />

      {/* OUR STORY */}
      <section className="about-story-section bg-white py-20 lg:py-28">
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-6 lg:grid-cols-2">
          <div className="order-2 lg:order-2" data-reveal>
            <PillBadge>Our Story</PillBadge>

            <h2 className="mt-4 font-heading text-3xl font-extrabold leading-[1.15] tracking-tight text-[#0B2748] sm:text-4xl">
              Prepared for the{" "}
              <span className="text-[#0785C7]">Unexpected</span>
            </h2>

            <div className="mt-6 space-y-4 text-[15.5px] leading-relaxed text-[#526A7D]">
              <p>
                Life is full of challenges and unexpected events. Before
                anything unpredictable happens, protect yourself and your
                family from financial loss with the right insurance
                coverage.
              </p>

              <p>
                Abhi Insurance Agency meets your insurance needs across
                Super Visa, visitor visa, life, critical illness and
                disability coverage. As your personal insurance advisor in
                Surrey BC, we help you prepare for the worst and take the
                worry and guesswork out of your financial protection.
              </p>

              <p>
                Buying insurance can feel daunting. Many people go without
                protection simply because they do not know what coverage
                they need, how much to spend, or where to go for support.
                There is no need to take that risk. We help you work through
                your options based on your circumstances today and your
                goals for the future.
              </p>

              <p className="font-semibold text-[#193B58]">
                We guarantee the lowest possible premiums, with up to 45%
                discounted rates. Your 100% satisfaction is guaranteed.
              </p>
            </div>
          </div>

          <div
            className="relative order-1 pb-16 lg:order-1 lg:pb-10"
            data-reveal
            style={{ transitionDelay: "120ms" }}
          >
            <div className="overflow-hidden rounded-3xl shadow-[0_18px_55px_rgba(7,59,103,0.14)]">
              <img
                src="https://images.pexels.com/photos/32206234/pexels-photo-32206234.jpeg?auto=compress&cs=tinysrgb&w=1200"
                alt="Happy family protected by insurance"
                className="h-[520px] w-full object-cover object-center transition-transform duration-700 hover:scale-105"
              />
            </div>

            <div className="absolute bottom-0 left-5 w-[210px] rounded-2xl border-4 border-white bg-[#006FB9] p-6 text-white shadow-[0_20px_50px_rgba(0,111,185,0.3)] sm:left-auto sm:right-8">
              <p className="font-heading text-3xl font-extrabold">
                100%
              </p>
              <p className="mt-1 max-w-[150px] text-sm leading-snug text-[#C5DEEC]">
                Satisfaction Guaranteed
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT DRIVES US */}
      <section className="about-values-section bg-white py-20 lg:py-24">
        <div className="mx-auto max-w-7xl px-6">
          <SectionHeading
            badge="What Drives Us"
            title={
              <>
                Our Mission and{" "}
                <span className="text-[#0785C7]">Values</span>
              </>
            }
          />

          <div
            data-reveal
            className={[
              "mt-12",
              "lg:overflow-hidden",
              "lg:rounded-[24px]",
              "lg:border lg:border-[#DCEAF2]",
              "lg:bg-white",
              "lg:shadow-[0_18px_55px_rgba(7,59,103,0.09)]",
              "lg:transition-all lg:duration-700",
              "lg:hover:shadow-[0_24px_65px_rgba(7,59,103,0.13)]",
            ].join(" ")}
          >
            <div className="grid grid-cols-1 gap-6 md:grid-cols-3 lg:gap-0 lg:px-8 lg:py-10">
              {VALUES.map((item, index) => {
                const Icon = item.icon;

                return (
                  <div
                    key={item.title}
                    data-reveal
                    style={{ transitionDelay: `${index * 110}ms` }}
                    className={[
                      "group rounded-2xl bg-white p-9",
                      "shadow-[0_2px_16px_rgba(0,111,185,0.07)]",
                      "transition-all duration-500",
                      "hover:-translate-y-1.5",
                      "hover:shadow-[0_20px_44px_rgba(0,111,185,0.13)]",
                      "lg:rounded-none lg:px-8 lg:py-2",
                      "lg:shadow-none",
                      "lg:hover:-translate-y-0 lg:hover:shadow-none",
                      index > 0
                        ? "lg:border-l lg:border-[#D8E8F1]"
                        : "",
                    ].join(" ")}
                  >
                    <span
                      className={[
                        "grid h-[52px] w-[52px] place-items-center",
                        "rounded-2xl bg-[#E2F2FA]",
                        "transition-all duration-500",
                        "group-hover:-translate-y-1",
                        "group-hover:rotate-6",
                        "group-hover:bg-[#006FB9]",
                        "group-hover:shadow-[0_12px_24px_rgba(0,111,185,0.2)]",
                      ].join(" ")}
                    >
                      <Icon className="h-6 w-6 text-[#006FB9] transition-all duration-500 group-hover:scale-110 group-hover:text-white" />
                    </span>

                    <h3 className="mt-5 font-heading text-xl font-bold text-[#0B2748] transition-colors duration-300 group-hover:text-[#006FB9]">
                      {item.title}
                    </h3>

                    <p className="mt-3 text-sm leading-relaxed text-[#526A7D]">
                      {item.text}
                    </p>

                    <span className="mt-6 hidden h-[3px] w-0 rounded-full bg-[#0785C7] transition-all duration-500 group-hover:w-16 lg:block" />
                  </div>
                );
              })}
            </div>

            <div
              className={[
                "mt-12 grid grid-cols-2 gap-6",
                "lg:mt-0 lg:grid-cols-4 lg:gap-0",
                "lg:border-t lg:border-[#DCEAF2]",
                "lg:bg-gradient-to-r",
                "lg:from-[#F1F9FD]",
                "lg:via-[#EAF6FC]",
                "lg:to-[#F1F9FD]",
              ].join(" ")}
            >
              <Milestone
                value={20}
                suffix="+"
                label="Years of Trusted Service"
                index={0}
              />
              <Milestone
                value={45}
                suffix="%"
                label="Premium Savings — Up To"
                index={1}
              />
              <Milestone
                value={8}
                suffix="x"
                label="'Top of the Table' Designation"
                index={2}
              />
              <Milestone
                value={7}
                suffix=""
                label="Days a Week Availability"
                index={3}
              />
            </div>
          </div>
        </div>
      </section>

      {/* LEADERSHIP */}
      <section className="about-leadership-section bg-white py-20 lg:py-28">
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-6 lg:grid-cols-2">
          <div className="order-2 lg:order-1" data-reveal>
            <div
              className={[
                "group overflow-hidden rounded-3xl bg-white",
                "border border-[#E1ECF3]",
                "shadow-[0_18px_55px_rgba(7,59,103,0.12)]",
                "transition-all duration-500",
                "hover:-translate-y-1",
                "hover:shadow-[0_24px_65px_rgba(7,59,103,0.17)]",
              ].join(" ")}
            >
              <div className="overflow-hidden">
               <img
  src={sandeepImage}
  alt="Sandeep Ahuja"
  className="h-[430px] w-full object-cover object-top transition-transform duration-700 group-hover:scale-105 sm:h-[500px]"
/>
              </div>

              <div className="relative border-t border-[#E1ECF3] bg-white px-6 py-5 text-center sm:px-7 sm:py-6 lg:text-left">
                <p className="font-heading text-lg font-bold text-[#0B2748] sm:text-xl">
                  {ADVISOR.name}
                </p>

                <p className="mt-1 text-sm leading-relaxed text-[#526A7D] sm:text-[15px]">
                  {ADVISOR.role}
                </p>
              </div>
            </div>
          </div>

          <div
            className="order-1 min-w-0 lg:order-2"
            data-reveal
            style={{ transitionDelay: "120ms" }}
          >
            <PillBadge>Leadership</PillBadge>

            <h2 className="mt-4 font-heading text-3xl font-extrabold leading-[1.15] tracking-tight text-[#0B2748] sm:text-4xl">
              Owned and Managed by{" "}
              <span className="text-[#0785C7]">Sandeep Ahuja</span>
            </h2>

            <div className="mt-5 space-y-4 text-[15.5px] leading-relaxed text-[#526A7D]">
              <p>
                Sandeep Ahuja began his career in insurance and financial
                services with RBC Insurance in 2000. Two years later he
                moved to independent broking, working with a range of
                insurance companies, and in 2009 he incorporated Abhi
                Insurance Agency with a clear aim: to give families across
                the Lower Mainland the best possible coverage at rates
                they can afford.
              </p>

              <p>
                He has been an MDRT member since 2010, has earned the Top
                of the Table designation 8 times, and is an Advocis member
                holding the CHS designation. Every recommendation starts
                with a detailed needs analysis, including a full Financial
                Needs Analysis for life insurance clients, followed by a
                comparison of what different insurers actually offer.
              </p>

              <p>
                Because insurance is only worth what it pays when a claim
                is made, the agency runs an in house Claims Assistance
                Department. When clients need to claim, they are not left
                to deal with an insurer alone.
              </p>
            </div>

           <ul className="aligned-icon-list mt-7 grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-flow-col sm:grid-cols-2 sm:grid-rows-3">
  {ABOUT_CREDENTIALS.map((credential) => (
    <li
      key={credential}
      className="flex items-center gap-3 text-[15px] font-medium text-[#193B58]"
    >
      <Trophy className="h-[18px] w-[18px] shrink-0 text-[#0785C7]" />
      {credential}
    </li>
  ))}
</ul>

            <div className="mt-9 flex flex-col items-stretch gap-4 sm:flex-row sm:flex-wrap sm:items-center">
              <Link
                to="/contact"
                className="btn-primary group justify-center"
                data-testid="about-book-consult"
              >
                Book a Free Consultation
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>

              <a
                href={ABOUT_PHONE_HREF}
                data-testid="about-advisor-call"
                className="group inline-flex min-h-[56px] items-center justify-center gap-3 rounded-2xl border border-[#CFE3EF] bg-[#F4F7FB] px-4 py-3 text-[14px] font-semibold text-[#006FB9] shadow-[0_2px_10px_rgba(0,111,185,0.04)] transition-colors duration-300 hover:border-[#006FB9] hover:bg-[#E2F2FA]"
              >
                <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-[#E2F2FA] transition-colors group-hover:bg-white">
                  <Phone
                    aria-hidden="true"
                    className="h-4 w-4"
                  />
                </span>

                <span>Talk to an Advisor</span>

                <ArrowUpRight
                  aria-hidden="true"
                  className="h-4 w-4 shrink-0 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* OUR PROMISE */}
      <section className="about-promise-section bg-white py-20 lg:py-24">
        <div className="mx-auto max-w-7xl px-6">
          <SectionHeading
            badge="Our Promise"
            title={
              <>
                Your Satisfaction is{" "}
                <span className="text-[#0785C7]">
                  100% Guaranteed
                </span>
              </>
            }
          />

          <div
            data-reveal
            className={[
              "mx-auto mt-12 grid max-w-4xl grid-cols-1 gap-4",
              "sm:grid-cols-2",
              "lg:relative lg:max-w-none lg:grid-cols-3 lg:gap-0",
              "lg:overflow-hidden lg:rounded-b-2xl",
              "lg:border lg:border-[#DCE8F0]",
              "lg:border-t-0 lg:bg-white",
              "lg:shadow-[0_16px_45px_rgba(7,59,103,0.09)]",
              "lg:transition-all lg:duration-700",
              "lg:hover:shadow-[0_24px_60px_rgba(7,59,103,0.13)]",
              "lg:before:absolute",
              "lg:before:left-0 lg:before:top-0",
              "lg:before:z-10",
              "lg:before:h-[5px] lg:before:w-full",
              "lg:before:origin-left",
              "lg:before:scale-x-0",
              "lg:before:bg-[#0785C7]",
              "lg:before:transition-transform",
              "lg:before:duration-1000",
              "lg:before:ease-out",
              "lg:data-[revealed=true]:before:scale-x-100",
            ].join(" ")}
          >
            {ABOUT_GUARANTEES.map((item, index) => {
              const isTopRow = [
                "consultations",
                "fees",
                "claims",
              ].includes(item.id);

              const hasDesktopLeftBorder = [
                "fees",
                "recognition",
                "claims",
                "refunds",
              ].includes(item.id);

              return (
                <div
                  key={item.id}
                  data-reveal
                  style={{ transitionDelay: `${index * 90}ms` }}
                  className={[
                    "group flex items-start gap-3.5",
                    "rounded-2xl bg-white p-6",
                    "shadow-sm",
                    "transition-all duration-500",
                    "hover:-translate-y-1",
                    "hover:shadow-[0_14px_34px_rgba(0,111,185,0.11)]",
                    GUARANTEE_DESKTOP_ORDER[item.id],
                    "lg:rounded-none lg:px-9 lg:py-10",
                    "lg:shadow-none",
                    "lg:hover:-translate-y-0",
                    "lg:hover:bg-[#F8FCFE]",
                    "lg:hover:shadow-none",
                    isTopRow
                      ? "lg:border-b lg:border-[#D8E6EE]"
                      : "",
                    hasDesktopLeftBorder
                      ? "lg:border-l lg:border-[#D8E6EE]"
                      : "",
                  ].join(" ")}
                >
                  <span
                    className={[
                      "about-promise-check-icon",
                      "mt-0.5 grid h-6 w-6 shrink-0",
                      "place-items-center rounded-full",
                      "transition-all duration-500",
                      "group-hover:scale-110",
                      "group-hover:bg-[#0785C7]",
                      "group-hover:shadow-[0_8px_18px_rgba(7,133,199,0.22)]",
                    ].join(" ")}
                  >
                    <CheckCircle2
                      className={[
                        "h-5 w-5 text-[#0785C7]",
                        "transition-all duration-500",
                        "group-hover:rotate-[360deg]",
                        "group-hover:text-white",
                      ].join(" ")}
                    />
                  </span>

                  <div className="min-w-0">
                    <p
                      className={[
                        "font-heading text-[15px] font-bold",
                        "text-[#0B2748]",
                        "transition-all duration-300",
                        "group-hover:translate-x-1",
                        "group-hover:text-[#0785C7]",
                      ].join(" ")}
                    >
                      {item.title}
                    </p>

                    <p className="mt-1.5 text-sm leading-relaxed text-[#526A7D]">
                      {item.text}
                    </p>

                    <span
                      className={[
                        "mt-5 hidden h-[3px] w-0 rounded-full",
                        "bg-[#0785C7]",
                        "transition-all duration-500",
                        "group-hover:w-14 lg:block",
                      ].join(" ")}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      

      {/* OUR LOCATION */}
      <AboutLocationSection />

      {/* FINAL CTA */}
      <CTASection
        title="Let's Protect What You've Built"
        subtitle={
          <>
            Free consultations 7 days a week. No hidden fees.{" "}
            <a href={ABOUT_PHONE_HREF}>
              Call {ABOUT_PHONE}.
            </a>
          </>
        }
      />

{/* DISCLAIMER */}
<section
  aria-label="Service and page disclaimer"
  className="bg-[#F4F7FB] py-8 lg:py-10"
>
  <div className="mx-auto max-w-7xl px-6">
    <div className="rounded-xl border-l-[5px] border-r-[5px] border-[#0B4FAF] bg-white px-5 py-5 shadow-[0_8px_24px_rgba(11,39,72,0.08)] sm:px-7 lg:px-8">
      
      {/* Short disclaimer for mobile */}
      <p className="text-sm leading-relaxed text-[#526A7D] md:hidden">
        {MOBILE_PAGE_DISCLAIMER}
      </p>

      {/* Complete disclaimer for tablet and desktop */}
      <p className="hidden text-sm leading-relaxed text-[#526A7D] md:block">
        {ABOUT_DISCLAIMER}
      </p>
      
    </div>
  </div>
</section>
    </div>
  );
}